import { useCallback, useEffect, useRef, useState } from "react";
import { finishGame, startGame, type GameResult } from "../api.js";

/** Duración de la partida, en segundos. */
const DURATION = 20;

/**
 * Cuánto tarda el marcador en cruzar la pista de lado a lado, en milisegundos.
 *
 * No es estética: el servidor rechaza más de 2 puntos por segundo como implausible
 * (`GAMES.aja.maxPointsPerSecond`). Con un barrido de 700ms, encadenar aciertos
 * perfectos da como mucho 1,4 por segundo, así que jugar bien nunca choca con la
 * validación — el límite se respeta por diseño del juego, no pidiéndole al jugador
 * que se contenga.
 */
const SWEEP_MS = 700;

/** Ancho inicial de la franja, en porcentaje de la pista. */
const BAND_START = 26;

/** Cuánto se estrecha en cada acierto, y hasta dónde. */
const BAND_STEP = 1.6;
const BAND_MIN = 6;

interface Props {
  onFinished: (result: GameResult) => void;
  onClose: () => void;
}

type Phase = "idle" | "playing" | "sending" | "done";

/**
 * El experimento del Parque ¡Ajá!: parar el péndulo dentro de la franja.
 *
 * Es de precisión y no de velocidad, al revés que el juego de prueba. Esa diferencia
 * es la que hace que el techo de puntos por segundo del servidor no le apriete: aquí
 * el ritmo lo pone el propio barrido, no los dedos del jugador.
 */
export function AjaGame({ onFinished, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [score, setScore] = useState(0);
  const [left, setLeft] = useState(DURATION);
  const [band, setBand] = useState(BAND_START);
  const [marker, setMarker] = useState(50);
  const [verdict, setVerdict] = useState<"hit" | "miss" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GameResult | null>(null);

  const sessionRef = useRef<string | null>(null);
  /** La posición viva del marcador. El estado es para pintar; esto es para decidir. */
  const markerRef = useRef(50);

  const begin = useCallback(async () => {
    setError(null);
    setScore(0);
    setLeft(DURATION);
    setBand(BAND_START);
    setVerdict(null);
    setResult(null);

    try {
      const started = await startGame("aja");
      sessionRef.current = started.sessionId;
      setPhase("playing");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo empezar.");
    }
  }, []);

  /*
   * El barrido va por reloj y no por fotogramas.
   *
   * Contar frames ata la velocidad al monitor: a 144Hz el péndulo iría al doble y el
   * juego sería otro. Con el tiempo transcurrido, el recorrido dura lo mismo en
   * cualquier pantalla — que además es lo que sostiene el cálculo de SWEEP_MS.
   */
  useEffect(() => {
    if (phase !== "playing") return;

    let frame = 0;
    const started = performance.now();

    const step = (now: number) => {
      const phaseOfSweep = ((now - started) % (SWEEP_MS * 2)) / SWEEP_MS;
      const position = phaseOfSweep <= 1 ? phaseOfSweep : 2 - phaseOfSweep;

      markerRef.current = position * 100;
      setMarker(markerRef.current);
      frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [phase]);

  // Cuenta atrás. Al llegar a cero se envía el puntaje.
  useEffect(() => {
    if (phase !== "playing") return;

    const timer = setInterval(() => {
      setLeft((remaining) => {
        if (remaining > 1) return remaining - 1;
        clearInterval(timer);
        setPhase("sending");
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase]);

  // El envío va en su propio efecto para no hacerlo dentro del setInterval.
  useEffect(() => {
    if (phase !== "sending") return;
    const sessionId = sessionRef.current;
    if (!sessionId) return;

    void (async () => {
      try {
        const finished = await finishGame(sessionId, score);
        setResult(finished);
        onFinished(finished);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "No se pudo cerrar la partida.");
      } finally {
        setPhase("done");
      }
    })();
  }, [phase, score, onFinished]);

  // El aviso de acierto o fallo se borra solo; si no, el último se queda fijo.
  useEffect(() => {
    if (!verdict) return;
    const timer = setTimeout(() => setVerdict(null), 450);
    return () => clearTimeout(timer);
  }, [verdict]);

  const stop = () => {
    if (phase !== "playing") return;

    const at = markerRef.current;
    const inside = Math.abs(at - 50) <= band / 2;

    if (!inside) {
      setVerdict("miss");
      return;
    }

    setScore((current) => current + 1);
    setBand((current) => Math.max(BAND_MIN, current - BAND_STEP));
    setVerdict("hit");
  };

  return (
    <div className="overlay">
      <div className="panel game">
        <div className="game__head">
          <h2 className="game__title">Experimento ¡Ajá!</h2>
          {phase === "playing" && <span className="game__timer">{left}s</span>}
          <span className="game__score">◉ {score}</span>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar juego">
            ×
          </button>
        </div>

        <div className="game__field">
          {phase === "idle" && (
            <div className="game__center">
              <p className="game__hint">
                Para el péndulo dentro de la franja. Cada acierto la hace más angosta.
              </p>
              <button className="btn" type="button" onClick={() => void begin()}>
                Empezar
              </button>
            </div>
          )}

          {phase === "playing" && (
            <div className="game__center">
              <div className="track">
                <div className="track__band" style={{ width: `${band}%` }} />
                <div className="track__marker" style={{ left: `${marker}%` }} />
              </div>

              <button className="btn" type="button" onPointerDown={stop}>
                ¡Ajá!
              </button>

              <p className="game__hint">
                {verdict === "hit" && "¡Dentro!"}
                {verdict === "miss" && "Fuera de la franja."}
                {!verdict && " "}
              </p>
            </div>
          )}

          {(phase === "sending" || phase === "done") && (
            <div className="game__center">
              {phase === "sending" && <p className="game__hint">Enviando…</p>}

              {result && (
                <>
                  <p className="game__result">+{result.coinsAwarded} monedas</p>
                  {result.cappedByLimit && (
                    <p className="game__hint">Alcanzaste el techo por hora.</p>
                  )}
                </>
              )}

              {phase === "done" && (
                <button className="btn" type="button" onClick={() => void begin()}>
                  Otra vez
                </button>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="game__error" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
